<template>
  <div>
    <v-dialog v-model="showArchiveReasonDialog" content-class="bg-snowy-white rounded-xl">
      <div class="pa-4"><h4>Are you sure you want to archive the submission? This action can not be reversed.</h4></div>
      <ArchiveNote :order-info="order" @on-archived="onArchived" />
    </v-dialog>
    <div class="card">
      <div v-if="order.orderSubmission?.submissionType" class="submission-type-chip">
        <EditableChipDropdown
          :options="[
            { label: 'CoverMyMeds', value: 'CoverMyMeds' },
            { label: 'eFax', value: 'eFax' },
          ]"
          :input-fields-config="submissionMethodFields"
          :dialog-title="'Change Submission Type'"
          :dialog-message="'Select a new submission type for this order.'"
          :disabled="false"
          :input-fields="[]"
          :api-endpoint="updateSubmissionMethod"
          :model-value="order.orderSubmission.submissionType"
          :form-data="{}"
          @change-confirmed="
            (newType) => {
              order.orderSubmission.submissionType = newType;
              order.status = 'Created';
            }
          " />
      </div>
      <div class="card-container">
        <div class="d-flex align-center ga-2 mb-6">
          <h2 class="lh--patient-name mb-0">
            {{ getPatientName(order) }}
          </h2>
          <v-btn
            icon
            variant="text"
            size="small"
            :color="detailsCopied ? 'success' : undefined"
            aria-label="Copy submission details"
            @click="onCopyDetails">
            <v-icon size="18">{{ detailsCopied ? "mdi-check" : "mdi-content-copy" }}</v-icon>
            <v-tooltip activator="parent" location="top">Copy details</v-tooltip>
          </v-btn>
        </div>
        <div class="lh--order-details">
          <div class="lh--order-details-item status-item">
            <div class="lh--order-details-item-label">Status:</div>
            <div class="lh--order-details-item-value">
              <div v-if="allowedToChangeStatus">
                <EditableChipDropdown
                  :options="[
                    { label: 'Approved', value: 'Approved' },
                    { label: 'Denied', value: 'Denied' },
                    { label: 'Prior authorization not required', value: 'Prior authorization not required' },
                  ]"
                  :input-fields-config="updateOrderStatusFields"
                  :dialog-title="'Change Submission Status'"
                  :dialog-message="'Select a new status for this submission.'"
                  :disabled="false"
                  :input-fields="[]"
                  :api-endpoint="updateOrderStatus"
                  :model-value="order.status || ''"
                  :color="getStatusLabelClass(order.status)"
                  :form-data="{}"
                  @change-confirmed="
                    (newStatus) => {
                      order.status = newStatus;
                    }
                  " />
              </div>
              <div v-else>
                <StatusLabel :text="order.status" :color="getStatusLabelClass(order.status)" :dot="true" />
              </div>
            </div>
            <div v-if="expiresSoon(order)" class="lh--order-details-item-value">
              <StatusLabel :text="expiresSoon(order)" color="error" :dot="false" />
            </div>
          </div>
          <div class="lh--order-details-item">
            <div class="lh--order-details-item-label">Medication:</div>
            <div class="lh--order-details-item-value">
              {{ titleCaseString(order.medication) }}
            </div>
          </div>
          <div class="lh--order-details-item">
            <div class="lh--order-details-item-label">Date Created:</div>
            <div class="lh--order-details-item-value">
              {{ convertDateFormat(order.createdOn) }}
            </div>
          </div>
          <div class="lh--order-details-item">
            <div class="lh--order-details-item-label">Insurance:</div>
            <div class="lh--order-details-item-value">
              {{ titleCaseString(order.insuranceProvider) || "—" }}
            </div>
          </div>
          <div class="lh--order-details-item">
            <div class="lh--order-details-item-label">Date Modified:</div>
            <div class="lh--order-details-item-value">
              {{ convertDateFormat(order.modifiedOn) }}
            </div>
          </div>
          <div class="lh--order-details-item">
            <div class="lh--order-details-item-label">Plan Type:</div>
            <div class="lh--order-details-item-value">
              {{ order.insurancePlanType || "—" }}
            </div>
          </div>
          <div class="lh--order-details-item">
            <div class="lh--order-details-item-label">Lamar Health Key:</div>
            <div class="lh--order-details-item-value">
              {{ order.orderKey }}
            </div>
          </div>
          <!-- When the payer is unknown, surface the selected form's name (CMM payer form, else eFax
               form) so ops can still tell which plan the PA is with. LAM-4462. -->
          <div
            v-if="!order.insuranceProvider && (order.portalSubmissionForm || order.faxDescription)"
            class="lh--order-details-item">
            <div class="lh--order-details-item-label">Submission Form:</div>
            <div class="lh--order-details-item-value">
              {{ order.portalSubmissionForm || order.faxDescription }}
            </div>
          </div>
          <div class="lh--order-details-item">
            <div class="lh--order-details-item-label">Prior Auth Number:</div>
            <div class="lh--order-details-item-value">
              {{ order.orderSubmission ? order.orderSubmission.priorAuthNumber || "-" : "-" }}
            </div>
          </div>

          <div v-if="order.prescriptionNumber" class="lh--order-details-item">
            <div class="lh--order-details-item-label">Rx Number:</div>
            <div class="lh--order-details-item-value">
              {{ order.prescriptionNumber }}
            </div>
          </div>

          <div v-if="order.orderSubmission && order.orderSubmission.portalSubmissionId" class="lh--order-details-item">
            <div class="lh--order-details-item-label">External Portal Key:</div>
            <div class="lh--order-details-item-value">
              {{ order.orderSubmission ? order.orderSubmission.portalSubmissionId : "-" }}
            </div>
          </div>

          <template v-if="order.externalSubmissionPortal">
            <div class="lh--order-details-item">
              <div class="lh--order-details-item-label">Submitted Outside System:</div>
              <div class="lh--order-details-item-value">
                {{ order.externalSubmissionPortal }}
              </div>
            </div>
            <div class="lh--order-details-item">
              <div class="lh--order-details-item-label">External Outcome:</div>
              <div class="lh--order-details-item-value">
                {{ titleCaseString(order.externalSubmissionOutcome) || "-" }}
              </div>
            </div>
            <div v-if="order.externalSubmissionReference" class="lh--order-details-item">
              <div class="lh--order-details-item-label">External Reference:</div>
              <div class="lh--order-details-item-value">
                {{ order.externalSubmissionReference }}
              </div>
            </div>
            <div v-if="order.externalSubmissionNotes" class="lh--order-details-item">
              <div class="lh--order-details-item-label">External Notes:</div>
              <div class="lh--order-details-item-value">
                {{ order.externalSubmissionNotes }}
              </div>
            </div>
          </template>

          <div v-if="order.orderSubmission && order.orderSubmission.priorAuthExpiration" class="lh--order-details-item">
            <div class="lh--order-details-item-label">Expires on:</div>
            <div class="lh--order-details-item-value">
              {{ order.orderSubmission ? convertDateFormat(order.orderSubmission.priorAuthExpiration) : "-" }}
            </div>
          </div>
          <div v-else class="lh--order-details-item"></div>

          <div
            v-if="
              order.orderSubmission &&
              ((order.statusNotes && order.statusNotes.length) || order.orderSubmission.operationalNotes)
            "
            class="lh--order-details-item w-100 ma-0">
            <div class="request-text">
              <div class="lh--order-details-item-label">Status Notes:</div>
              <div
                v-if="order.statusNotes && order.statusNotes.length"
                class="lh--order-details-item-value status-notes-display">
                <div v-for="(note, i) in order.statusNotes" :key="i" class="status-notes-display__note">
                  <div class="status-notes-display__meta">
                    <span
                      class="status-notes-display__author"
                      :class="{ 'status-notes-display__author--system': !note.author }">
                      {{ note.author || "System" }}
                    </span>
                    <span v-if="formatNoteTimestamp(note.createdOn)" class="status-notes-display__time">
                      {{ formatNoteTimestamp(note.createdOn) }}
                    </span>
                  </div>
                  <StatusNoteText :text="note.note" />
                </div>
              </div>
              <div
                v-if="order.orderSubmission.operationalNotes"
                class="lh--order-details-item-value status-notes-display">
                <div class="status-notes-display__sublabel">Operational Notes</div>
                <StatusNoteText :text="order.orderSubmission.operationalNotes" />
              </div>
            </div>
          </div>

          <!-- <div class="lh--order-details-item"></div> -->

          <div
            v-if="
              order.orderSubmission &&
              (order.orderSubmission.responseDocumentUrl ||
                (order.orderSubmission.responseDocuments && order.orderSubmission.responseDocuments.length))
            "
            class="lh--order-details-item w-100 ma-0">
            <div class="request-text">
              <div class="lh--order-details-item-label">Payor Response:</div>
              <div class="lh--order-details-item-value">
                <ul v-if="order.orderSubmission.responseDocumentUrl">
                  <li>
                    <a :href="order.orderSubmission.responseDocumentUrl" class="text-color-blue-gray" target="_blank">
                      View Decision
                    </a>
                  </li>
                </ul>
                <ul v-if="order.orderSubmission.responseDocuments && order.orderSubmission.responseDocuments.length">
                  <li v-for="(doc, index) in order.orderSubmission.responseDocuments" :key="index">
                    <a :href="doc.link" class="text-color-blue-gray" target="_blank">
                      {{ doc.label }} (Received on {{ convertDateFormat(doc.timestamp) }})
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="props.buttonsVisible" class="buttons-container">
        <span v-if="order.status === 'Initiated'">
          <v-btn @click="selectForCompleting(order)">Complete Form</v-btn>
        </span>
        <span v-else>
          <v-btn :disabled="!order.staffEditOverride && order.status === 'Created'" @click="selectSubmission(order)">
            View Details
          </v-btn>
        </span>
        <v-btn variant="outlined" @click="toggleModal">Archive</v-btn>
        <v-btn
          v-if="
            order.orderSubmission?.submissionType === 'CoverMyMeds' &&
            (order.status === 'Payor/Medication not supported' || order.status === 'Response received')
          "
          variant="outlined"
          @click="selectForReopen(order)">
          Re-open
        </v-btn>
      </div>
    </div>
    <div v-if="order.canUploadDecision" class="buttons-container">
      <hr class="light-hr" />
      Upload Decision Document for Order: {{ order.orderKey }} - {{ getPatientName(order) }}
      <span>
        <UploadFileComponent :download-label="order.orderKey" @download-link="uploadDecisionFile" />
      </span>
      <span v-if="error" class="error-text">{{ error }}</span>
      <hr class="light-hr" />
    </div>
    <v-snackbar v-model="showCopyError" color="error" timeout="5000" location="top">
      Couldn't copy details to clipboard.
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useSelectedSubmissionStore } from "@/stores/selectedSubmissionStore";
import { storeToRefs } from "pinia";
import { convertDateFormat, titleCaseString, formatNoteTimestamp } from "@/utils";
import { formatSubmissionDetails, copyTextToClipboard } from "@/utils/submissionClipboard";
import { orderInfoService } from "@/services/orderInfoService";
import UploadFileComponent from "@/components/UploadFileComponent";
import StatusLabel from "@/components/StatusLabel.vue";
import StatusNoteText from "@/components/StatusNoteText.vue";
import EditableChipDropdown from "./EditableChipDropdown.vue";
import ArchiveNote from "@/components/ArchiveNote.vue";

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  buttonsVisible: {
    type: Boolean,
    default: true,
  },
  faxForms: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["onDelete", "onUploadDecision"]);

const router = useRouter();
const { selectedSubmission } = storeToRefs(useSelectedSubmissionStore());
const order = ref(props.order);
const error = ref(null);
const showArchiveReasonDialog = ref(false);
const detailsCopied = ref(false);
const showCopyError = ref(false);

async function onCopyDetails() {
  const copied = await copyTextToClipboard(formatSubmissionDetails(order.value));
  if (copied) {
    detailsCopied.value = true;
    setTimeout(() => (detailsCopied.value = false), 1500);
  } else {
    showCopyError.value = true;
  }
}

const allowedToChangeStatus = computed(() => {
  return [
    "Approved",
    "Sent to prescriber",
    "Sent to plan",
    "Response received",
    "Denied",
    "No payor response received",
    "Prior authorization not required",
    "Submitted outside our system",
  ].includes(order.value.status);
});

async function uploadDecisionFile(downloadLink) {
  error.value = null;
  try {
    await orderInfoService.uploadDecisionDocument(downloadLink.label, { decisionFileUrl: downloadLink.link });
    emit("onUploadDecision", { order: order.value });
  } catch (e) {
    error.value = "Error uploading decision document: " + downloadLink.label + ". Please try again. " + e;
  }
}

async function updateSubmissionMethod(newType) {
  await orderInfoService.changeSubmissionMethod(order.value.orderKey, {
    submissionMethod: newType.value,
    faxSubmissionFormDetail: newType.fax_submission_form_detail ? newType.fax_submission_form_detail : null,
  });
  return newType;
}

const submissionMethodFields = {
  eFax: [
    {
      key: "fax_submission_form_detail",
      label: "Fax Form",
      type: "select",
      required: true,
      placeholder: "Please select a fax form",
      options: props.faxForms,
      optionLabel: "description",
      optionValue: "faxDestination",
      rules: [(v) => !!v || "Fax form is required"],
    },
  ],
};

const updateOrderStatusFields = {
  Denied: [
    {
      key: "notes",
      label: "Notes",
      type: "textarea",
      required: false,
      placeholder: "Please provide any additional notes (optional)",
      rules: [],
    },
  ],
  Approved: [
    {
      key: "prior_auth_number",
      label: "Prior Auth Number",
      type: "text",
      required: false,
      placeholder: "Please provide the prior auth number (optional)",
      rules: [],
    },
    {
      key: "prior_auth_expiration",
      label: "Prior Auth Expiration Date",
      type: "date",
      required: false,
      placeholder: "Please provide the prior auth expiration date (optional)",
      rules: [],
    },
    {
      key: "notes",
      label: "Notes",
      type: "textarea",
      required: false,
      placeholder: "Please provide any additional notes (optional)",
      rules: [],
    },
  ],
  "Prior authorization not required": [
    {
      key: "notes",
      label: "Notes",
      type: "textarea",
      required: false,
      placeholder: "Please provide any additional notes (optional)",
      rules: [],
    },
  ],
};

const updateOrderStatus = async (newStatus) => {
  await orderInfoService.changeRecordedStatus(order.value.orderKey, { ...newStatus });
  return newStatus;
};

function getPatientName(order) {
  return order?.patientFirstName && order?.patientLastName
    ? `${order.patientFirstName} ${order.patientLastName}`
    : "Patient Information Form Not Completed";
}

function selectForReopen(order) {
  selectedSubmission.value = order;
  router.push({ name: "check-my-coverage", params: { id: selectedSubmission.value.orderKey } });
}

function selectForCompleting(order) {
  selectedSubmission.value = order;
  router.push({ name: "create-submission", params: { id: selectedSubmission.value.orderKey } });
}

function selectSubmission(order) {
  selectedSubmission.value = order;
  router.push({ name: "check-my-coverage", params: { id: selectedSubmission.value.orderKey } });
}

const expiresSoon = (order) => {
  if (order.orderSubmission?.priorAuthExpiration) {
    const expirationDate = new Date(order.orderSubmission.priorAuthExpiration);
    const today = new Date();
    const timeDifference = expirationDate.getTime() - today.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
    if (daysDifference > 0) {
      return "";
    }
    if (daysDifference < 0) {
      return "Expired";
    }
    if (daysDifference <= 7) {
      return "Expires soon!";
    }
  } else {
    return "";
  }
};

const getStatusLabelClass = (status) => {
  const statusClasses = {
    Processing: "blue-gray",
    Created: "blue-gray",
    "Sent to prescriber": "blue-gray",
    "Sent to plan": "blue-gray",
    "Sent to plan by prescriber": "blue-gray",
    "Response received": "success",
    Denied: "error",
    Incomplete: "warning",
    Approved: "success",
    "Prior authorization not required": "success",
    "Submitted outside our system": "info",
    Initiated: "warning",
    "Requires more information": "warning",
    "No payor response received": "warning",
    "Payor/Medication not supported": "error",
    Archived: "blue-gray",
    Expired: "blue-gray",
    default: "info",
  };

  return statusClasses[status] || statusClasses.default;
};

const toggleModal = () => {
  showArchiveReasonDialog.value = true;
};

const onArchived = () => {
  showArchiveReasonDialog.value = false;
  emit("onDelete", { order: props.order });
};
</script>

<style lang="scss" scoped>
@import "../styles/_variables.scss";
@import "../styles/typography.scss";
@import "../styles/pages/_submission-status.scss";

.skeleton-name {
  margin-bottom: 16px;
}

.v-skeleton-loader__button {
  margin-right: 8px;
}

.card {
  position: relative;
}

.submission-type-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 8px;
}

// On a phone the absolute chip overlaps the patient name; it's an edit
// affordance, so hide it (the card is read-only on mobile anyway).
@media (max-width: 600px) {
  .submission-type-chip {
    display: none;
  }
}

.archive-dialog {
  background-color: $lh_color_snowy_white;
}

.status-notes-display {
  word-break: break-word;
  margin-top: 4px;

  & + .status-notes-display {
    margin-top: 12px;
  }
}

.status-notes-display__note {
  & + .status-notes-display__note {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid $lh_dash_border;
  }
}

.status-notes-display__meta {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 3px;
}

.status-notes-display__author {
  font-weight: 600;
  color: $lh_color_navy;

  &--system {
    color: $lh_color_blue_gray;
  }
}

.status-notes-display__time {
  font-family: $lh_font_mono;
  font-size: 11px;
  color: $lh_color_blue_gray;
  white-space: nowrap;
}

.status-notes-display__sublabel {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $lh_color_blue_gray;
  margin-bottom: 2px;
}
</style>
